import type { UseConferenceCallManagerReturn } from './index.types';
import { CONFERENCE_HELD_ID_PREFIX } from 'Modules/softphone/hooks/useMultipleCallsManager/index.constants';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { createLogger } from 'Modules/softphone/utils/logger';
import { useEffect, useMemo } from 'react';

const logger = createLogger('useConferenceCallManager');

/**
 * Hook for managing conference call state.
 *
 * Provides the active conference session, its participants (filtered from active calls),
 * and derived flags. Participants are sorted alphabetically by displayName.
 *
 * The list only reflects calls that were merged locally and is never updated by
 * server-side changes. See {@link ConferenceSession} for details.
 *
 * @returns Conference state, participants array, and derived flags
 */
export const useConferenceCallManager = (): UseConferenceCallManagerReturn => {
  const { activeCalls, activeConference } = useSoftphoneStore((state) => ({
    activeCalls: state.activeCalls,
    activeConference: state.activeConference,
  }));

  const isConferenceActive =
    activeConference !== null &&
    activeConference !== undefined &&
    activeConference.conferenceState === 'active';

  const participants = useMemo(() => {
    if (!activeConference) {
      return [];
    }

    const conferenceParticipants = Array.from(activeCalls.values())
      .filter(
        (call) =>
          call.conferenceId === activeConference.roomGuid &&
          !call.id.startsWith(CONFERENCE_HELD_ID_PREFIX)
      )
      .sort((a, b) => {
        const nameA = (a.displayName ?? '').toLowerCase();
        const nameB = (b.displayName ?? '').toLowerCase();
        return nameA.localeCompare(nameB);
      });

    logger.debug('Derived conference participants', {
      conferenceId: activeConference.roomGuid,
      count: conferenceParticipants.length,
      callIds: conferenceParticipants.map((c) => c.id),
    });

    return conferenceParticipants;
  }, [activeCalls, activeConference]);

  const participantCount = participants.length;

  useEffect(() => {
    logger.debug('useConferenceCallManager state', {
      isConferenceActive,
      participantCount,
      conferenceState: activeConference?.conferenceState ?? null,
    });
  }, [isConferenceActive, participantCount, activeConference?.conferenceState]);

  return {
    isConferenceActive,
    conference: activeConference ?? null,
    participants,
    participantCount,
  };
};
